import { FC, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Loader } from "@/components/ui/loader";

import { PromptLibraryItem, PromptLibraryItemSource, PromptLibraryTab } from "../promptLibrary.types";
import { useGetPromptLibraryItemsQuery } from "../promptLibrary.api";
import { usePromptLibraryContext } from "../usePromptLibraryContext";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { createGetItemsPayload } from "@/modules/promptLibrary/promptLibrary.utils";
import { PromptLibraryRightDrawerTabs } from "@/modules/promptLibrary/components/promptLibraryRightDrawerTabs";
import { PromptLibraryCard } from "@/modules/promptLibrary/components/promptLibraryCard";

interface Props {
  source: PromptLibraryItemSource;
}

export const PromptLibraryRightDrawer: FC<Props> = ({ source }) => {
  const [activeTab, setActiveTab] = useState<PromptLibraryTab>("all");
  const [editingPromptItem, setEditingPromptItem] = useState<PromptLibraryItem | null>(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const getItemsPayload = createGetItemsPayload(searchPhrase, activeTab);
  const { data, isLoading } = useGetPromptLibraryItemsQuery(getItemsPayload);
  const userIds = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.map(item => item.user_id);
  }, [data]);
  const userQuery = useGetDirectoryUsersQuery(userIds);

  const { emit } = usePromptLibraryContext();

  const handleTabClick = (tab: PromptLibraryTab) => {
    setActiveTab(tab);
  };

  const filteredData = data?.filter(item => item.source === source) || [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PromptLibraryRightDrawerTabs onTabClick={handleTabClick}>
        <div className="flex items-center gap-2 p-4">
          <input
            type="text"
            placeholder="Search prompts..."
            className="w-full rounded-lg border border-input bg-background p-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-desert-sand-500"
            value={searchPhrase}
            onChange={e => setSearchPhrase(e.target.value)}
          />
          <button
            type="button"
            className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </PromptLibraryRightDrawerTabs>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {filteredData.map(item => {
            const user = userQuery.data?.find(({ id }) => id === item.user_id);
            const userName = user ? `${user.name} ${user.surname}` : undefined;

            return <PromptLibraryCard key={item.id} item={item} userName={userName} onSendToChat={emit} />;
          })}

          {filteredData.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">No prompts found</p>
          )}
        </div>
      </div>

      {editingPromptItem && (
        <ModifyNewPromptLibraryItemDialog
          isOpen={!!editingPromptItem}
          setIsOpen={() => setEditingPromptItem(null)}
          item={editingPromptItem}
          mode="edit"
        />
      )}

      <ModifyNewPromptLibraryItemDialog
        isOpen={isAddDialogOpen}
        setIsOpen={setIsAddDialogOpen}
        mode="add"
        item={{ prompt: "", source }}
      />
    </div>
  );
};
